import type { DefaultPaymentLogEditHandoffDefaultPaymentEditForm } from '../types/pages'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams } from 'react-router-dom'
import {
  CheckCircle2,
  Clock,
  CreditCard,
  DollarSign,
  Download,
  FileCheck,
  FileText,
  Landmark,
  Loader2,
  PieChart,
  Printer,
  User,
  X,
} from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { useBreadcrumbPageTitle } from '@/components/layout/DashboardBreadcrumbContext'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DatePicker } from '@/components/ui/DatePicker'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import { ReportOverviewMetrics } from '../components/ReportOverviewMetrics'
import styles from '../styles/paymentLogDetail.module.css'
import { useUserRole } from '@/hooks/useUserRole'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import {
  generateDefaultPaymentStatement,
  sendDefaultPaymentStatementEmail,
} from '@/features/reimbursements/services/reimbursementsService'
import { openServeFileInNewTab } from '@/lib/utils/serveFileHelpers'
import {
  fetchApplicationInfo,
  fetchRentalList,
  fetchResidentDefault,
} from '@/features/resident/services/residentApi'
import { toFormValues } from '@/features/resident/hooks/useResidentDetail'
import { normalizeResidentDetailDocuments } from '@/features/resident/utils/residentDocuments'
import { buildSettlementGeneratePayload } from '@/features/resident/utils/residentPayload'
import { DefaultPaymentCompletedInfoCard } from '../components/DefaultPaymentCompletedInfoCard'
import {
  canShowPaymentCheckForm,
  PaymentLogAchCheckPaymentForm,
} from '../components/PaymentLogActionForms'
import { completeDefaultPaymentReimbursement, getPaymentLogDetail, updateDefaultPaymentLog } from '../services/reportsService'
import type { DefaultPaymentLogUpdatePayload, PaymentLogDetailResponse } from '../types/api'
import { buildPaymentLogExportUrls } from '../utils/paymentLogExportUrls'


function toYmd(value: unknown): string {
  if (value == null || value === '') return ''
  const raw = String(value)
  const numeric = Number(raw)
  const date =
    Number.isFinite(numeric) && raw.length <= 10
      ? new Date(numeric * 1000)
      : new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function htmlBreaksToText(value: unknown): string {
  return String(value ?? '').replace(/<br\s*\/?>/gi, '\n')
}

function textToHtmlBreaks(value: string): string {
  return value ? value.replace(/\n/g, '<br/>') : ''
}

function padApplicantId(value: unknown): string {
  if (value == null || value === '') return ''
  return String(value).padStart(8, '0')
}

function isDefaultPayment(detail: PaymentLogDetailResponse | null): boolean {
  return String(detail?.payment_type ?? '').includes('DEFAULT')
}

function isRefundPayment(detail: PaymentLogDetailResponse | null): boolean {
  const type = String(detail?.payment_type ?? '')
  return type.includes('REFUND') || type === 'DEFAULT'
}

function toNumber(value: unknown): number {
  const n = Number(String(value ?? 0).replace(/[^0-9.-]+/g, ''))
  return Number.isFinite(n) ? n : 0
}

/** Splits camelCase/underscore status codes into title case, e.g. `PendingPaymentAuthorization` -> `Pending Payment Authorization`. */
function titleCaseStatus(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw) return '—'
  if (raw === 'ACH_Check_Pending') return raw
  const spaced = raw.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function renderStatusBadge(status: unknown) {
  const raw = String(status ?? '').toLowerCase()
  const label = titleCaseStatus(status)
  if (raw.includes('pending') || raw.includes('fail') || raw.includes('error') || raw.includes('reject')) {
    return (
      <span className={styles.statusBadgeWarning}>
        <Clock className="h-3.5 w-3.5" aria-hidden />
        {label}
      </span>
    )
  }
  if (raw.includes('complete') || raw.includes('success') || raw.includes('approved')) {
    return <span className={styles.statusBadgeSuccess}>{label}</span>
  }
  return <span className={styles.statusBadgeDefault}>{label}</span>
}

/** Dynamic Gross Amount subtitle derived from the actual payment type (not a fixed string). */
function paymentTypeSubtitle(type: unknown): string {
  const raw = String(type ?? '').trim()
  if (!raw) return ''
  return `${titleCaseStatus(raw)} payment`
}

function paymentDetailRows(detail: PaymentLogDetailResponse) {
  const paymentAmount =
    detail?.payment_type === 'BALANCE_ADJUSTMENT'
      ? detail?.adjustment_amount
      : detail?.payment_amount
  const serviceCost =
    detail?.payment_type === 'DEFAULT'
      ? toNumber(detail?.payment_amount) + (toNumber(detail?.refund_fee) * -1)
      : detail?.surcharge_fee && detail?.payment_type === 'CONTRACT'
        ? toNumber(detail?.payment_amount) - toNumber(detail?.surcharge_fee)
        : paymentAmount
  const serviceFee =
    detail?.payment_type === 'CONTRACT' && detail?.surcharge_fee
      ? detail.surcharge_fee
      : detail?.payment_type === 'DEFAULT' && detail?.payment_source === 'Paper'
        ? 15
        : 0
  const rows: Array<readonly [string, unknown]> = [
    [detail?.payment_type === 'DEFAULT' ? 'Net Due' : 'Service Cost', serviceCost],
    ['Payment Service Fee', serviceFee],
    ['Sales Tax', detail?.sales_tax ?? 0],
  ]
  if (detail?.refund_fee) {
    rows.push([
      detail?.payment_type === 'DEFAULT' ? 'Paper Check Fee' : 'Less Refund Fee',
      detail.refund_fee,
    ])
  }
  rows.push(['Total', paymentAmount])
  return rows
}

function initialForm(detail: PaymentLogDetailResponse): DefaultPaymentLogEditHandoffDefaultPaymentEditForm {
  const refund = isRefundPayment(detail)
  const paymentBy = refund ? detail?.payer_info : detail?.payment_to
  return {
    special_note: String(detail?.special_note ?? ''),
    check_number: String(detail?.auth_code ?? ''),
    payment_date: toYmd(detail?.created_at) || toYmd(new Date()),
    paid_to: htmlBreaksToText(detail?.payment_to),
    payment_by: htmlBreaksToText(paymentBy),
    is_reissue_payment: String(detail?.is_reissue_payment ?? '') === '1',
    reissue_payment_date: toYmd(detail?.reissue_payment_date),
    reissue_check_number: String(detail?.reissue_check_number ?? ''),
  }
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </span>
    </div>
  )
}

function PaymentBreakdownCard({
  detail,
  fullHeight = false,
}: {
  detail: PaymentLogDetailResponse
  fullHeight?: boolean
}) {
  return (
    <div className={fullHeight ? styles.cardSectionFullHeight : styles.cardSection}>
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon}>
          <PieChart className="h-5 w-5" aria-hidden />
        </div>
        <h3 className={styles.cardTitle}>Payment breakdown</h3>
      </div>
      <div>
        {paymentDetailRows(detail).map(([label, value], index, rows) => {
          const isTotal = index === rows.length - 1
          return (
            <div key={label} className={isTotal ? styles.totalBox : styles.infoRow}>
              <span className={styles.infoLabel}>{label}</span>
              <span className={styles.infoValue}>
                {formatAmount(value as string | number | null | undefined)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MultilineValue({ value }: { value: string }) {
  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  if (!lines.length) return <p className="text-sm text-slate-800">—</p>

  return (
    <div className="space-y-1 text-sm text-slate-800">
      {lines.map((line, index) => (
        <p key={`${line}-${index}`} className="break-words">
          {line}
        </p>
      ))}
    </div>
  )
}

function StatementDialog({
  isOpen,
  title,
  onClose,
  children,
}: {
  isOpen: boolean
  title: string
  onClose: () => void
  children: ReactNode
}) {
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/35 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="statement-dialog-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-700"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <div className="border-b border-slate-100 bg-slate-50/80 px-6 py-5 pr-16">
          <h2
            id="statement-dialog-title"
            className="text-lg font-semibold text-slate-900"
          >
            {title}
          </h2>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

/** React replacement for Angular `PaymentLogsDetailEditComponent`. */
export function DefaultPaymentLogEditHandoffPage() {
  const { logId } = useParams<{ logId: string }>()
  const role = useUserRole()
  useBreadcrumbPageTitle('Edit Default Payment Log')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [statementLoading, setStatementLoading] = useState(false)
  const [statementChoiceOpen, setStatementChoiceOpen] = useState(false)
  const [statementEmailOpen, setStatementEmailOpen] = useState(false)
  const [generatedStatementPath, setGeneratedStatementPath] = useState<string | null>(null)
  const [statementEmailOptions, setStatementEmailOptions] = useState({
    include_statement: true,
    include_instruction: false,
    include_signup_instruction: false,
  })
  const [selectedStatementAction, setSelectedStatementAction] = useState<
    'generate' | 'open' | 'email' | null
  >(null)
  const [statementData, setStatementData] = useState<{
    defaultId: string | number
    defaultData?: Record<string, unknown>
    calculationData?: Record<string, unknown>
    propertyAch?: boolean
    existingStatementPath?: string | null
  } | null>(null)
  const [detail, setDetail] = useState<PaymentLogDetailResponse | null>(null)
  const [form, setForm] = useState<DefaultPaymentLogEditHandoffDefaultPaymentEditForm | null>(null)

  useEffect(() => {
    if (!logId) return
    setLoading(true)
    void getPaymentLogDetail(logId)
      .then((data) => {
        setDetail(data)
        setForm(initialForm(data))
      })
      .catch(showApiError)
      .finally(() => setLoading(false))
  }, [logId])

  const { downloadUrl, printUrl } = useMemo(
    () =>
      logId
        ? buildPaymentLogExportUrls(logId, 'payment_log')
        : { downloadUrl: '', printUrl: '' },
    [logId],
  )

  const patch = (partial: Partial<DefaultPaymentLogEditHandoffDefaultPaymentEditForm>) =>
    setForm((prev) => (prev ? { ...prev, ...partial } : prev))

  const refreshDetail = async () => {
    if (!logId) return
    const refreshed = await getPaymentLogDetail(logId)
    setDetail(refreshed)
    setForm(initialForm(refreshed))
  }

  const submit = async () => {
    if (!logId || !form || !detail?.payment_log_id) return
    const payload: DefaultPaymentLogUpdatePayload = {
      special_note: form.special_note.trim(),
      check_number: form.check_number.trim(),
      payment_date: form.payment_date,
      paid_to: textToHtmlBreaks(form.paid_to),
      payment_by: textToHtmlBreaks(form.payment_by),
      is_reissue_payment: form.is_reissue_payment ? '1' : '0',
      reissue_payment_date: form.is_reissue_payment
        ? form.reissue_payment_date
        : '',
      reissue_check_number: form.is_reissue_payment
        ? form.reissue_check_number.trim()
        : '',
    }
    setSaving(true)
    try {
      await updateDefaultPaymentLog(detail.payment_log_id, payload)
      showSuccess('Payment Information Updated Successfully!')
      setConfirmOpen(false)
      const refreshed = await getPaymentLogDetail(logId)
      setDetail(refreshed)
      setForm(initialForm(refreshed))
    } catch (err) {
      showApiError(err)
    } finally {
      setSaving(false)
    }
  }

  const openStatementFlow = async () => {
    if (!detail?.applicant_id || !detail?.user_id) {
      showError('Unable to open statement options because resident information is missing.')
      return
    }

    setStatementLoading(true)
    try {
      const [application, rentals] = await Promise.all([
        fetchApplicationInfo(detail.applicant_id),
        fetchRentalList().catch(() => null),
      ])
      const rawResidentDetail = await fetchResidentDefault(
        detail.user_id,
        application?.application_id,
      )
      const residentDetail = normalizeResidentDetailDocuments(rawResidentDetail)
      const defaultId = residentDetail.resident_default?.fk_resident_default_id
      if (!defaultId) {
        showError('Default Settlement Statement cannot be generated because default record is missing.')
        return
      }

      const values = toFormValues(residentDetail, application, rentals)
      const payload = buildSettlementGeneratePayload({
        values,
        residentDetail,
        applicationData: application,
        applicationId: application?.application_id,
        roleName: role.roleName,
      })

      setStatementData({
        defaultId,
        defaultData: payload.default_data as Record<string, unknown>,
        calculationData: payload.calculation_data as Record<string, unknown>,
        propertyAch: Boolean(
          (residentDetail.resident_default as Record<string, unknown> | undefined)?.property_ach,
        ),
        existingStatementPath:
          typeof residentDetail.document?.default_statement === 'string'
            ? residentDetail.document.default_statement
            : null,
      })
      setStatementEmailOptions({
        include_statement: true,
        include_instruction: false,
        include_signup_instruction: !(residentDetail.resident_default as Record<string, unknown> | undefined)?.property_ach,
      })
      setGeneratedStatementPath(null)
      setSelectedStatementAction(null)
      setStatementChoiceOpen(true)
    } catch (err) {
      showApiError(err)
    } finally {
      setStatementLoading(false)
    }
  }

  const extractStatementPath = (response: unknown): string | null => {
    if (typeof response === 'string') return response
    if (response && typeof response === 'object') {
      const data = response as Record<string, unknown>
      const path =
        data.default_statement ??
        data.statement ??
        data.path ??
        data.file ??
        data.url ??
        data.message
      return typeof path === 'string' && path.trim() ? path : null
    }
    return null
  }

  const openStatementPath = async (path: string | null | undefined) => {
    if (!path) {
      showError('Default Settlement Statement Not Generated Yet')
      return
    }
    const s3Key = String(path).replace(/^https?:\/\/[^/]+\/?/, '')
    await openServeFileInNewTab(`/serve-file-claims/Statement/?url=${encodeURIComponent(s3Key)}`)
  }

  const generateStatement = async () => {
    if (!statementData) return
    setSelectedStatementAction('generate')
    setStatementLoading(true)
    try {
      const response = await generateDefaultPaymentStatement(statementData.defaultId, {
        default_data: statementData.defaultData,
        calculation_data: statementData.calculationData,
      })
      showSuccess('Default Settlement Statement Generated Successfully')
      setStatementChoiceOpen(false)
      const statementPath = extractStatementPath(response)
      setGeneratedStatementPath(statementPath)
      setStatementEmailOpen(true)
      if (logId) {
        const refreshed = await getPaymentLogDetail(logId)
        setDetail(refreshed)
        setForm(initialForm(refreshed))
      }
    } catch (err) {
      showApiError(err)
    } finally {
      setStatementLoading(false)
    }
  }

  const closeStatementEmail = () => {
    setStatementEmailOpen(false)
    setGeneratedStatementPath(null)
  }

  const finishStatementEmail = async (type: 'none' | 'property') => {
    if (!statementData) return
    setStatementLoading(true)
    try {
      if (type === 'property') {
        await sendDefaultPaymentStatementEmail(statementData.defaultId, {
          type: 'property',
          ...statementEmailOptions,
        })
        showSuccess('Default Settlement Statement Email Sent Successfully')
      }
      closeStatementEmail()
      if (generatedStatementPath) {
        window.setTimeout(() => {
          void openStatementPath(generatedStatementPath).catch(showApiError)
        }, 500)
      }
    } catch (err) {
      showApiError(err)
    } finally {
      setStatementLoading(false)
    }
  }

  const completeReimbursement = async () => {
    if (!logId || !form || !detail?.payment_log_id) return
    setCompleting(true)
    try {
      await completeDefaultPaymentReimbursement(detail.payment_log_id, {
        check_number: form.check_number.trim(),
        payment_date: form.payment_date,
      })
      showSuccess('Payment Status Updated Successfully!')
      setCompleteConfirmOpen(false)
      const refreshed = await getPaymentLogDetail(logId)
      setDetail(refreshed)
      setForm(initialForm(refreshed))
    } catch (err) {
      showApiError(err)
    } finally {
      setCompleting(false)
    }
  }

  const refund = isRefundPayment(detail)
  const defaultPayment = isDefaultPayment(detail)
  const showInlineCheckForm = canShowPaymentCheckForm(
    role.roleName,
    String(detail?.payment_status ?? ''),
  )
  const showCompletedInfo =
    role.isLibertyRole &&
    defaultPayment &&
    detail?.payment_status === 'Completed'

  return (
    <PageShell
      title="Edit Default Payment Log"
      contentClassName="!space-y-5 w-full max-w-none min-w-0 px-4 sm:px-6 lg:px-8 xl:px-10"
      headerAction={
        downloadUrl || printUrl ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {downloadUrl ? (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-[rgb(21,151,255)] shadow-sm transition hover:bg-slate-50"
                aria-label="Download payment receipt"
                title="Download payment receipt"
              >
                <Download className="h-5 w-5" aria-hidden />
              </a>
            ) : null}
            {printUrl ? (
              <a
                href={printUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-[rgb(21,151,255)] shadow-sm transition hover:bg-slate-50"
                aria-label="Print payment receipt"
                title="Print payment receipt"
              >
                <Printer className="h-5 w-5" aria-hidden />
              </a>
            ) : null}
          </div>
        ) : null
      }
    >
      {loading ? (
        <div className="flex min-h-[12rem] items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-hidden />
        </div>
      ) : detail && form ? (
        <>
          <ConfirmDialog
            isOpen={confirmOpen}
            title="Confirm"
            message="Update payment information?"
            primaryLabel={saving ? 'Saving...' : 'Continue'}
            onPrimary={submit}
            secondaryLabel="Cancel"
            onSecondary={() => setConfirmOpen(false)}
            onDismiss={() => setConfirmOpen(false)}
          />
          <ConfirmDialog
            isOpen={completeConfirmOpen}
            title="Confirm"
            message="Confirm that You want to Complete Reimbursement now?"
            primaryLabel={completing ? 'Completing...' : 'Continue'}
            onPrimary={completeReimbursement}
            secondaryLabel="Cancel"
            onSecondary={() => setCompleteConfirmOpen(false)}
            onDismiss={() => setCompleteConfirmOpen(false)}
          />
          <StatementDialog
            isOpen={statementChoiceOpen}
            onClose={() => setStatementChoiceOpen(false)}
            title="Default Settlement Statement"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                disabled={statementLoading}
                onClick={() => void generateStatement()}
                className={`inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-5 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none ${
                  selectedStatementAction === 'generate'
                    ? 'border border-[rgb(21,151,255)] bg-[rgb(21,151,255)] text-white hover:bg-[rgb(0,132,230)]'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-md'
                }`}
              >
                {statementData?.existingStatementPath ? 'ReGenerate' : 'Generate'}
              </button>
              {statementData?.existingStatementPath ? (
                <>
                  <button
                    type="button"
                    disabled={statementLoading}
                    onClick={() => {
                      setSelectedStatementAction('open')
                      void openStatementPath(statementData.existingStatementPath)
                    }}
                    className={`inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-5 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none ${
                      selectedStatementAction === 'open'
                        ? 'border border-[rgb(21,151,255)] bg-[rgb(21,151,255)] text-white hover:bg-[rgb(0,132,230)]'
                        : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-md'
                    }`}
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    disabled={statementLoading}
                    onClick={() => {
                      setSelectedStatementAction('email')
                      setStatementChoiceOpen(false)
                      setGeneratedStatementPath(null)
                      setStatementEmailOpen(true)
                    }}
                    className={`inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-5 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none ${
                      selectedStatementAction === 'email'
                        ? 'border border-[rgb(21,151,255)] bg-[rgb(21,151,255)] text-white hover:bg-[rgb(0,132,230)]'
                        : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-md'
                    }`}
                  >
                    Email To
                  </button>
                </>
              ) : null}
            </div>
          </StatementDialog>

          <StatementDialog
            isOpen={statementEmailOpen}
            onClose={closeStatementEmail}
            title="Email To"
          >
            <div className="space-y-5 text-sm text-slate-800">
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Include Attachments
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={statementEmailOptions.include_statement}
                      onChange={(event) =>
                        setStatementEmailOptions((prev) => ({
                          ...prev,
                          include_statement: event.target.checked,
                        }))
                      }
                    />
                    Default Settlement Statement
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={statementEmailOptions.include_instruction}
                      onChange={(event) =>
                        setStatementEmailOptions((prev) => ({
                          ...prev,
                          include_instruction: event.target.checked,
                        }))
                      }
                    />
                    Default Instructions
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={statementEmailOptions.include_signup_instruction}
                      onChange={(event) =>
                        setStatementEmailOptions((prev) => ({
                          ...prev,
                          include_signup_instruction: event.target.checked,
                        }))
                      }
                    />
                    ACH Sign-up Instructions
                  </label>
                </div>
              </div>
              <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  disabled={statementLoading}
                  onClick={() => void finishStatementEmail('none')}
                  className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
                >
                  None
                </button>
                <button
                  type="button"
                  disabled={statementLoading}
                  onClick={() => void finishStatementEmail('property')}
                  className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-[rgb(21,151,255)] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
                >
                  Property
                </button>
              </div>
            </div>
          </StatementDialog>

          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault()
              setConfirmOpen(true)
            }}
          >
            <ReportOverviewMetrics
              title="Transaction overview"
              subtitle="Payment summary"
              testIdPrefix="default-payment-log-detail"
              items={[
                {
                  id: 'status',
                  label: 'Status',
                  value: titleCaseStatus(detail.payment_status),
                  icon: <CheckCircle2 aria-hidden />,
                  tone: 'green',
                },
                {
                  id: 'gross-amount',
                  label: 'Gross Amount',
                  value: formatAmount(detail.payment_amount),
                  secondaryValue: paymentTypeSubtitle(detail.payment_type) || undefined,
                  icon: <DollarSign aria-hidden />,
                  tone: 'blue',
                },
                {
                  id: 'payment-source',
                  label: 'Payment Source',
                  value: (detail.payment_source || '—').toString().toUpperCase(),
                  icon: <CreditCard aria-hidden />,
                  tone: 'violet',
                },
                {
                  id: 'application-id',
                  label: 'Application ID',
                  value: detail.applicant_id ? (
                    <Link
                      to={`/application/${detail.applicant_id}`}
                      className="text-[#1597ff] hover:underline"
                    >
                      {padApplicantId(detail.applicant_id)}
                    </Link>
                  ) : (
                    '—'
                  ),
                  icon: <FileText aria-hidden />,
                  tone: 'sky',
                },
              ]}
            />

            <div className={styles.mainGrid}>
              <div className={styles.cardSectionFullHeight}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <CreditCard className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Payment information</h3>
                </div>
                <div>
                  <InfoRow
                    label={`Payment ${refund ? 'Refund' : 'Invoice'} for`}
                    value={`${detail.first_name ?? ''} ${detail.last_name ?? ''}`.trim()}
                  />
                  {defaultPayment ? (
                    <InfoRow label="Unit #" value={detail.unit_number} />
                  ) : null}
                  <InfoRow label="Payment status" value={renderStatusBadge(detail.payment_status)} />
                  <InfoRow
                    label="Payment source"
                    value={<span className={styles.sourceBadge}>{detail.payment_source || '—'}</span>}
                  />
                  {defaultPayment ? (
                    <>
                      <InfoRow
                        label="Payee verified"
                        value={
                          <span className="inline-flex items-center gap-1.5">
                            {detail.verify === '1' ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                            ) : null}
                            {detail.verify === '1' ? 'Yes' : 'No'}
                            {detail.company_id != null ? (
                              <a
                                href={`/company/edit/${detail.company_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-1 text-[#1597ff] hover:underline"
                              >
                                Verify now
                              </a>
                            ) : null}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Pay to"
                        value={detail.pay_to === 'COMPANY' ? 'Company' : 'Property'}
                      />
                    </>
                  ) : (
                    <InfoRow label="IP address" value={detail.ip_address} />
                  )}
                  {detail.card_number ? (
                    <InfoRow label="Card #" value={detail.card_number} />
                  ) : null}
                  <InfoRow
                    label="Payment date"
                    value={
                      <DatePicker
                        className={styles.fieldInput}
                        value={form.payment_date}
                        disabled={detail.payment_status === 'ACH_Check_Pending'}
                        onChange={(value) => patch({ payment_date: value })}
                        allowFutureDates
                        dataTestId="default-payment-log-edit-payment-date"
                        placeholder="Select payment date"
                      />
                    }
                  />
                </div>
              </div>

              <div className={styles.cardSectionFullHeight}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <FileCheck className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Settlement information</h3>
                </div>
                <div className="space-y-4">
                  <InfoRow label="Payment type" value={detail.payment_type} />
                  <label className="block space-y-1.5">
                    <span className={styles.fieldLabel}>
                      {['ACH', 'PAPER_CHECK'].includes(String(detail.payment_source))
                        ? 'Check / ACH #'
                        : 'Transaction'}
                    </span>
                    <input
                      className={styles.fieldInput}
                      value={form.check_number}
                      disabled={detail.payment_status === 'ACH_Check_Pending'}
                      onChange={(e) => patch({ check_number: e.target.value })}
                      placeholder="Enter check or ACH number"
                    />
                  </label>
                  <label className="block space-y-1.5">
                    <span className={styles.fieldLabel}>Special note</span>
                    <textarea
                      className={styles.fieldTextarea}
                      value={form.special_note}
                      onChange={(e) => patch({ special_note: e.target.value })}
                      placeholder="Add a note for this transaction"
                    />
                  </label>
                  {defaultPayment ? (
                    <div className={styles.toggleRow}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={form.is_reissue_payment}
                          onChange={(e) => patch({ is_reissue_payment: e.target.checked })}
                          aria-label="Mark this payment as re-issued"
                        />
                        <span className={styles.toggleTrack}>
                          <span className={styles.toggleThumb} />
                        </span>
                      </label>
                      <span className={styles.toggleRowLabel}>
                        Re-issued payment
                        <span className="ml-1 font-normal text-slate-500">
                          Mark this payment as re-issued.
                        </span>
                      </span>
                    </div>
                  ) : null}
                  {defaultPayment && form.is_reissue_payment ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="block space-y-1.5">
                        <span className={styles.fieldLabel}>Re-issued payment date</span>
                        <DatePicker
                          className={styles.fieldInput}
                          value={form.reissue_payment_date}
                          onChange={(value) => patch({ reissue_payment_date: value })}
                          allowFutureDates
                          dataTestId="default-payment-log-edit-reissue-payment-date"
                          placeholder="Select re-issued payment date"
                        />
                      </label>
                      <label className="block space-y-1.5">
                        <span className={styles.fieldLabel}>Re-issued check #</span>
                        <input
                          className={styles.fieldInput}
                          value={form.reissue_check_number}
                          onChange={(e) => patch({ reissue_check_number: e.target.value })}
                        />
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex justify-start">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl bg-[rgb(21,151,255)] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : null}
                Submit
              </button>
            </div>
          </form>

          <div className="space-y-5">
            <div className={showInlineCheckForm ? 'grid gap-5 md:grid-cols-2' : 'grid gap-5 md:grid-cols-2 xl:grid-cols-3'}>
              <div className={styles.cardSection}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <Landmark className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Paid {refund ? 'to' : 'by'}</h3>
                </div>
                <MultilineValue value={form.paid_to} />
              </div>
              <div className={styles.cardSection}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <User className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Payment {refund ? 'by' : 'to'}</h3>
                </div>
                <MultilineValue value={form.payment_by} />
              </div>
              {showInlineCheckForm ? null : <PaymentBreakdownCard detail={detail} />}
            </div>

            {showInlineCheckForm ? (
              <div className={styles.mainGrid}>
                <PaymentBreakdownCard detail={detail} fullHeight />
                <PaymentLogAchCheckPaymentForm
                  logData={detail}
                  onRefresh={() => {
                    void refreshDetail()
                  }}
                />
              </div>
            ) : null}

            <div className="grid gap-5 xl:grid-cols-2">
              {showCompletedInfo ? (
                <DefaultPaymentCompletedInfoCard
                  detail={detail}
                  statementLoading={statementLoading}
                  completing={completing}
                  onStatementClick={openStatementFlow}
                  onCompleteClick={() => setCompleteConfirmOpen(true)}
                />
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <p className="text-center text-slate-500">Payment log not found.</p>
      )}
    </PageShell>
  )
}
